import qs from 'query-string';
import axios from 'axios';
import type { AuthOptions } from 'auth0-js';
import { config } from './env.config';
import { resolveAuthDomainFromFlag, resolveLoginAuthDomainFromFlag, resolveSignupAuthDomainFromFlag } from './authDomainFlag';
import { addAction } from '@shared/utils/logger';
import { importWithRetry } from '@shared/core/chunkRetry';
import { entryPoint, recordAuthFailure, retryUntilLoaded, runCallbackFlow, runRedirectFlow, toLoadFailure } from './authFailure';
import type { AuthAuthorizeRedirect, AuthAuthorizeResult, AuthSignupArgs, AuthCallback, AuthError } from './auth.types';

// Re-exported under `Auth0`-prefixed names for back-compat with existing
// imports from `@static/js/auth0` (and `@static/js/joy`, which forwards them).
export type { AuthAuthorizeRedirect as Auth0AuthorizeRedirect, AuthAuthorizeResult as Auth0AuthorizeResult, AuthSignupArgs as Auth0SignupArgs } from './auth.types';

const CLIENT_ID = config.auth0ClientId;
const REDIRECT_URI = `${config.clientUri}/account-callback`;
const CUSTOM_DOMAIN = 'accounts.withjoy.com';
const AUTH0_DOMAIN = 'withjoy.auth0.com';
const IDENTITY_SERVER_URI = `${config.serverUri}/identity`;
/**
 * Currently we are using 2 domains for auth0. `withjoy.auth0.com` is the one provided by auth0 and
 * joy custom domain `accounts.withjoy.com`.
 * The reason we need both is because the legacy monorepo uses the one provided by auth0 and we are
 * very reluctant to modifying stuff there because of legacy code and apis being used.
 * The custom domain we need because our it's a necessity to have a tailored experience for our users
 * when they sign up so we must use a custom domain to avoid issues with 3rd party cookies Safari and
 * probably Chrome very soon.
 *
 * !!! So for now, unless it is sign up or login, any auth0 action should use `withjoy.auth0.com`
 */
// The host `webAuth` was actually built with, captured for telemetry. Read from here rather than off
// the WebAuth instance so we don't depend on auth0-js internals (`baseOptions.domain`).
let resolvedWebAuthDomain: string | null = null;

/**
 * Fire-and-forget record of which auth flow ran on which host.
 *
 * `authDomainResolved` (authDomainFlag.ts) reports what the flag resolved to once per page load;
 * this reports what was actually *invoked*. That matters because `authorize`, social login and
 * logout are full-page redirects — New Relic never sees them as AjaxRequests, so without this
 * they're invisible. Only `changePassword` shows up on its own (it's an XHR).
 *
 * Hostname, flow name, the page the flow was started from (`entry`, see `entryPoint`: a fixed list,
 * `event/<page>` for event pages, else `other`, so no event handle or query string) and, for a direct
 * social login, the connection name. No PII, no await.
 */
// `entry` is read when the flow is called: by the time auth0-js has loaded, the page may have moved on.
const trackAuthFlow = (flow: string, domain: string | null, entry: string, extra?: Record<string, string | boolean>) => {
  addAction('authFlowInvoked', { flow, domain: domain ?? 'unresolved', entry, ...extra });
};

const AUTH_OPTIONS: AuthOptions = {
  // Login and signup each pick their host from their own flag and fall back to this one.
  // `audience` is an Auth0 API identifier (not a network host) and is left unchanged.
  domain: CUSTOM_DOMAIN,
  clientID: CLIENT_ID,
  redirectUri: REDIRECT_URI,
  audience: 'https://withjoy.auth0.com/userinfo',
  responseType: 'code',
  scope: 'openid offline_access',
};

// auth0-js is a browser library and as such, is not instantiated on the server during SSR.
// Normal imports at the top of the file would result in a error log.
// https://github.com/auth0/auth0.js/issues/877#issuecomment-665743985
// Removing the magic webpackChunkName because it's interfering with
// webpack's internal chunking algorithm.
//
// Each loader below shares one in-flight attempt and forgets a failure, so the next auth action
// retries instead of inheriting a dead promise for the rest of the page life.
const loadAuth0Js = retryUntilLoaded(() =>
  importWithRetry(() => import('auth0-js'), 'auth0-js').catch((error: unknown) => {
    throw toLoadFailure(error);
  }),
);

// Social login. Pinned to Auth0's own host because the legacy app looks for the session there (see
// the note on the two domains above). Going direct also keeps Auth0 scoring the user's IP, not the
// auth proxy's egress address, which reads as a datacenter and draws an MFA challenge a social user
// cannot answer.
const loadAuth0DomainWebAuth = retryUntilLoaded(() => loadAuth0Js().then(({ WebAuth }) => new WebAuth({ ...AUTH_OPTIONS, domain: AUTH0_DOMAIN })));

// A client whose host comes from a flag, or `fallback` when the flag has no answer or the lookup
// rejects. `onResolved` records the host actually used, for telemetry.
const loadFlaggedWebAuth = (resolveDomain: () => Promise<string | null>, fallback: string, onResolved: (domain: string) => void) =>
  retryUntilLoaded(() =>
    loadAuth0Js().then(({ WebAuth }) =>
      resolveDomain()
        .then(
          flagDomain => flagDomain ?? fallback,
          () => fallback,
        )
        .then(domain => {
          const webAuth = new WebAuth({ ...AUTH_OPTIONS, domain });
          onResolved(domain);
          return webAuth;
        }),
    ),
  );

// authorize, changePassword and logout use redirect / `/dbconnections/change_password`
// / `/v2/logout`, which the auth proxy implements. Their host comes from the
// `auth-domain-joy-web-social-logout` flag, falling back to today's Auth0 host.
const loadWebAuth = loadFlaggedWebAuth(resolveAuthDomainFromFlag, AUTH0_DOMAIN, domain => {
  resolvedWebAuthDomain = domain;
});

// Signup posts to `/dbconnections/signup`, which the auth proxy implements. Falls back to the Auth0
// custom domain, so this is inert until the flag is ramped.
let resolvedSignupWebAuthDomain: string = CUSTOM_DOMAIN;
const loadSignupWebAuth = loadFlaggedWebAuth(resolveSignupAuthDomainFromFlag, CUSTOM_DOMAIN, domain => {
  resolvedSignupWebAuthDomain = domain;
});

// Login posts to `/co/authenticate` and then redirects to `/authorize`. The proxy serves both, but only
// for client ids in its CROSS_ORIGIN_AUTH_CLIENT_IDS. Same fallback as signup.
let resolvedLoginWebAuthDomain: string = CUSTOM_DOMAIN;
const loadLoginWebAuth = loadFlaggedWebAuth(resolveLoginAuthDomainFromFlag, CUSTOM_DOMAIN, domain => {
  resolvedLoginWebAuthDomain = domain;
});

/** Starts loading auth0-js early. Never rejects: a failed load is retried by the next auth action. */
export const initAuth0 = (): Promise<void> =>
  Promise.all([loadLoginWebAuth(), loadAuth0DomainWebAuth(), loadWebAuth(), loadSignupWebAuth()]).then(
    () => undefined,
    failure => recordAuthFailure('init', failure),
  );

const socialLogin = (connection: string, redirect: AuthAuthorizeRedirect) => {
  const entry = entryPoint();
  runRedirectFlow('socialLogin', loadAuth0DomainWebAuth, webAuth => {
    trackAuthFlow('socialLogin', AUTH0_DOMAIN, entry, { connection });
    webAuth.authorize({ connection, redirectUri: REDIRECT_URI, state: JSON.stringify(redirect) });
  });
};

// `enableSocialSignIn` marks the /social-login route, the other way into social login, so it shares
// the social pin. Every other caller keeps the flag.
const authorize = (params?: Record<string, string | boolean | number>) => {
  const isSocial = params?.enableSocialSignIn === true;
  const entry = entryPoint();
  runRedirectFlow('authorize', isSocial ? loadAuth0DomainWebAuth : loadWebAuth, webAuth => {
    trackAuthFlow('authorize', isSocial ? AUTH0_DOMAIN : resolvedWebAuthDomain, entry, { socialRoute: isSocial });
    webAuth.authorize({ redirectUri: REDIRECT_URI, ...params });
  });
};

const deriveAuthorizeResult = (urlSearch: string): AuthAuthorizeResult => {
  const parsedUrl = qs.parse(urlSearch);
  const code = parsedUrl['code'] as string;
  const redirectJSON = parsedUrl['state'] as string;
  return { code, redirectJSON };
};

const hydrateAuthorizeRedirect = (state: string): AuthAuthorizeRedirect => {
  // the caller must `try { } catch { }` as needed
  return JSON.parse(state);
};

/**
 * Wraps a callback flow's callback to record its outcome as `authFlowResult`. Error shape only, no
 * message body, so no PII. `statusCode` is the field that matters: a real HTTP response
 * (400/401/403/429/5xx) means the request reached the host, whereas 0 or absent means it never
 * completed (a CORS rejection, network failure, or auth0-js never loading). Those look identical from
 * the outside and are the first thing to check when a flow breaks against a new host.
 */
const recordFlowResult =
  <T>(flow: string, domain: () => string | null, cb: AuthCallback<T, AuthError>): AuthCallback<T, AuthError> =>
  (err, res) => {
    const failure = err as { code?: string; name?: string; statusCode?: number } | null | undefined;
    addAction('authFlowResult', {
      flow,
      domain: domain() ?? 'unresolved',
      ok: !err,
      ...(err ? { errorCode: failure?.code ?? failure?.name ?? 'unknown', statusCode: failure?.statusCode ?? 0 } : {}),
    });
    cb(err, res);
  };

// A successful login redirects away without calling back, so login only ever records failures.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const login = (email: string, password: string, cb: AuthCallback<any, AuthError>) => {
  const entry = entryPoint();
  const recordResult = recordFlowResult('login', () => resolvedLoginWebAuthDomain, cb);
  runCallbackFlow('login', loadLoginWebAuth, recordResult, (loginWebAuth, done) => {
    trackAuthFlow('login', resolvedLoginWebAuthDomain, entry);
    loginWebAuth.login({ realm: 'Username-Password-Authentication', email, password }, done);
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const signup = (args: AuthSignupArgs, cb: AuthCallback<any, AuthError>) => {
  const entry = entryPoint();
  runCallbackFlow('signup', loadSignupWebAuth, cb, (signupWebAuth, done) => {
    trackAuthFlow('signup', resolvedSignupWebAuthDomain, entry);
    signupWebAuth.signup({ connection: 'Username-Password-Authentication', ...args }, done);
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const changePassword = (email: string, cb: AuthCallback<any, AuthError>) => {
  const recordResult = recordFlowResult('changePassword', () => resolvedWebAuthDomain, cb);
  const entry = entryPoint();
  runCallbackFlow('changePassword', loadWebAuth, recordResult, (webAuth, done) => {
    trackAuthFlow('changePassword', resolvedWebAuthDomain, entry);
    webAuth.changePassword({ connection: 'Username-Password-Authentication', email }, done);
  });
};

const signout = (returnToUrl?: string) => {
  const entry = entryPoint();
  runRedirectFlow('signout', loadWebAuth, webAuth => {
    trackAuthFlow('signout', resolvedWebAuthDomain, entry);
    webAuth.logout({ returnTo: returnToUrl });
  });
};

const refreshToken = (code: string, shouldSkipTokenDelegation = false) => {
  return axios.post(`${IDENTITY_SERVER_URI}/refreshToken`, { redirectUri: REDIRECT_URI, code, skipDelegate: shouldSkipTokenDelegation });
};

export const auth0 = {
  authorize,
  socialLogin,
  deriveAuthorizeResult,
  hydrateAuthorizeRedirect,
  login,
  signup,
  signout,
  changePassword,
  refreshToken,
};

export type Auth0 = typeof auth0;
